import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  LogOut,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  Search,
  Filter,
  Trash2,
  Home
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AdminDashboardPage = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, booking: null });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/admin/login");
    } else if (!authLoading && user?.role !== "admin") {
      toast.error("Access denied");
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/bookings/stats`),
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status: newStatus });
      toast.success(`Booking ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async () => {
    if (!deleteDialog.booking) return;
    try {
      await axios.delete(`${API}/admin/bookings/${deleteDialog.booking.id}`);
      toast.success("Booking deleted");
      setDeleteDialog({ open: false, booking: null });
      fetchData();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-blue-100 text-blue-800 border-blue-200",
      completed: "bg-green-100 text-green-800 border-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <AlertCircle className="h-4 w-4" />,
      confirmed: <CheckCircle className="h-4 w-4" />,
      completed: <CheckCircle className="h-4 w-4" />,
      cancelled: <XCircle className="h-4 w-4" />,
    };
    return icons[status] || <AlertCircle className="h-4 w-4" />;
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client_phone?.includes(searchTerm) ||
      booking.service_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-spa-neutral-50 flex items-center justify-center">
        <div className="animate-pulse text-spa-neutral-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spa-neutral-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-spa-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="font-heading text-xl md:text-2xl text-spa-neutral-800 italic">
                Admin Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-spa-neutral-600 hover:text-spa-pink-600">
                  <Home className="h-4 w-4 mr-2" />
                  View Site
                </Button>
              </Link>
              <span className="text-sm text-spa-neutral-500 hidden sm:block">
                {user?.email}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-spa-neutral-600 hover:text-red-600 hover:border-red-200"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="border-0 spa-shadow">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-spa-neutral-800">{stats.total}</div>
                <div className="text-sm text-spa-neutral-500">Total Bookings</div>
              </CardContent>
            </Card>
            <Card className="border-0 spa-shadow bg-yellow-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
                <div className="text-sm text-yellow-600">Pending</div>
              </CardContent>
            </Card>
            <Card className="border-0 spa-shadow bg-blue-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-700">{stats.confirmed}</div>
                <div className="text-sm text-blue-600">Confirmed</div>
              </CardContent>
            </Card>
            <Card className="border-0 spa-shadow bg-green-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
                <div className="text-sm text-green-600">Completed</div>
              </CardContent>
            </Card>
            <Card className="border-0 spa-shadow bg-spa-pink-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-spa-pink-700">{stats.today}</div>
                <div className="text-sm text-spa-pink-600">Today</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 spa-shadow mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-spa-neutral-400" />
                <Input
                  placeholder="Search by name, phone, or service..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-spa-neutral-200"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 border-spa-neutral-200" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2 text-spa-neutral-400" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={fetchData}
                className="border-spa-neutral-200"
                data-testid="refresh-btn"
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card className="border-0 spa-shadow">
          <CardHeader className="border-b border-spa-neutral-100">
            <CardTitle className="font-heading text-xl text-spa-neutral-800">
              Bookings ({filteredBookings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredBookings.length === 0 ? (
              <div className="p-12 text-center text-spa-neutral-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No bookings found</p>
              </div>
            ) : (
              <div className="divide-y divide-spa-neutral-100">
                {filteredBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-4 md:p-6 hover:bg-spa-neutral-50 transition-colors"
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Booking Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-medium text-spa-neutral-800">
                            {booking.service_name}
                          </h3>
                          <Badge
                            className={`${getStatusColor(booking.status)} border flex items-center gap-1`}
                          >
                            {getStatusIcon(booking.status)}
                            {booking.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-spa-neutral-600">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-spa-pink" />
                            {booking.client_name}
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-spa-pink" />
                            <a href={`tel:${booking.client_phone}`} className="hover:text-spa-pink-600">
                              {booking.client_phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-spa-pink" />
                            {booking.booking_date}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-spa-pink" />
                            {booking.booking_time}
                          </div>
                        </div>
                        
                        {booking.client_email && (
                          <div className="flex items-center gap-2 text-sm text-spa-neutral-500">
                            <Mail className="h-4 w-4 text-spa-pink" />
                            <a href={`mailto:${booking.client_email}`} className="hover:text-spa-pink-600">
                              {booking.client_email}
                            </a>
                          </div>
                        )}
                        
                        {booking.notes && (
                          <div className="flex items-start gap-2 text-sm text-spa-neutral-500">
                            <FileText className="h-4 w-4 text-spa-pink mt-0.5" />
                            <span className="italic">{booking.notes}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-spa-neutral-200"
                              data-testid={`status-dropdown-${booking.id}`}
                            >
                              Update Status
                              <ChevronDown className="h-4 w-4 ml-2" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => updateBookingStatus(booking.id, "pending")}
                              className="text-yellow-600"
                            >
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Mark Pending
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => updateBookingStatus(booking.id, "confirmed")}
                              className="text-blue-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirm
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => updateBookingStatus(booking.id, "completed")}
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Complete
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => updateBookingStatus(booking.id, "cancelled")}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, booking })}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-btn-${booking.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, booking: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this booking for{" "}
              <strong>{deleteDialog.booking?.client_name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, booking: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteBooking}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
